// src/pages/ServiceManagement/ServiceDetails.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { servicesApi } from '../../services/apiClient';
import OverviewTab from './tabs/OverviewTab';
import VersionsTab from './tabs/VersionsTab';
import UpdatePathsTab from './tabs/UpdatePathsTab';

const ServiceDetails = ({ service, onUpdate, onDelete }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [serviceData, setServiceData] = useState(service);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setServiceData(service);
    setHasChanges(false);
  }, [service]);

  const handleDataChange = (updates) => {
    setServiceData(prev => ({ ...prev, ...updates }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await servicesApi.update(service.id, serviceData);

      if (response.data.success) {
        enqueueSnackbar('Service updated successfully', { variant: 'success' });
        setHasChanges(false);
        onUpdate();
      }
    } catch (error) {
      console.error('Error updating service:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to update service',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${service.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await servicesApi.delete(service.id);

      if (response.data.success) {
        onDelete();
      }
    } catch (error) {
      console.error('Error deleting service:', error);
      enqueueSnackbar(
        error.response?.data?.error || 'Failed to delete service',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          <Typography variant="h6">{service.friendlyName || service.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            Service ID: {service.id} | UUID: {service.uuid}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={!hasChanges || loading}
          >
            Save Changes
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="Overview" />
          <Tab label="Versions" />
          <Tab label="Update Paths" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {activeTab === 0 && (
          <OverviewTab
            service={serviceData}
            onChange={handleDataChange}
          />
        )}
        {activeTab === 1 && (
          <VersionsTab
            serviceId={service.id}
            onUpdate={onUpdate}
          />
        )}
        {activeTab === 2 && (
          <UpdatePathsTab
            serviceId={service.id}
            onUpdate={onUpdate}
          />
        )}
      </Box>
    </Box>
  );
};

export default ServiceDetails;
